/**
 * Image compression and S3 upload utilities
 * Extracted from AddCanvasTemplate.js to reduce file size
 */
import imageCompression from "browser-image-compression";
import { BASE_URL } from "../../../config";
import { stripBakedInCheckerboardFromImageBlob } from "./checkerboardAlphaUtils";

/**
 * Pass to `addImage(..., imageAddOptions)` when loading raster files so the same
 * checkerboard strip as paste/drop can run (blob pipeline in coreCanvasHandlers).
 */
export const CANVAS_FILE_UPLOAD_ADD_IMAGE_OPTS = {
  isOnTopLayer: true,
  stripCheckerboard: true,
};

/**
 * Remove baked-in UI checkerboard from raster uploads (PNG/WebP/JPEG) when detected.
 * Original upload bytes for S3 can stay unchanged — use this for the **display** copy only.
 * @param {File|Blob} file
 * @returns {Promise<File|Blob>}
 */
export async function preprocessRasterImageForCanvas(file) {
  if (!file) return file;
  const type = (file.type || "").toLowerCase();
  if (!/^image\/(png|webp|jpeg|jpg)/.test(type)) {
    return file;
  }
  try {
    const out = await stripBakedInCheckerboardFromImageBlob(file);
    if (!out || out === file || out.size === 0) return file;
    const baseName =
      file instanceof File && file.name
        ? file.name.replace(/\.(jpe?g|webp)$/i, ".png")
        : "image.png";
    return new File([out], baseName, { type: "image/png" });
  } catch {
    return file;
  }
}

/**
 * Helper function to convert color values to hex format
 * @param {string|number} colorValue - Color value (hex string or numeric)
 * @returns {string} Hex color string
 */
export const convertColorValue = (colorValue) => {
  if (typeof colorValue === "number" && colorValue > 0) {
    return `#${colorValue.toString(16).padStart(6, "0")}`;
  }
  return colorValue || "#000000";
};

/**
 * Compress a base64 image to reduce file size
 * @param {string} base64Image - Base64 image data URL
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<string>} Compressed base64 image
 */
export const compressImage = (
  base64Image,
  maxWidth = 800,
  maxHeight = 600,
  quality = 0.7
) => {
  return new Promise((resolve) => {
    // If not a data URL, return as is
    if (!base64Image || !base64Image.startsWith("data:image")) {
      resolve(base64Image);
      return;
    }

    const img = new Image();
    img.onload = () => {
      // Create canvas for resizing
      const canvas = document.createElement("canvas");

      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = Math.round(width * (maxHeight / height));
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Get compressed image
      const compressedImage = canvas.toDataURL("image/jpeg", quality);
      resolve(compressedImage);
    };

    img.onerror = () => {
      // If there's an error, return the original
      resolve(base64Image);
    };

    img.src = base64Image;
  });
};

/**
 * Upload base64 data to S3 using presigned URL
 * @param {string} base64Data - Base64 encoded file data
 * @param {string} fileName - Name of the file
 * @param {string} fileType - MIME type of the file
 * @returns {Promise<string>} Permanent S3 URL
 */
export const uploadToS3 = async (base64Data, fileName, fileType,folder="canvas",checkLenght=true) => {
  try {
    // Validate base64 data
    if (!base64Data || typeof base64Data !== "string") {
      throw new Error("Invalid base64 data provided");
    }

    // Check if base64 data is too long (over 50MB to be safe)
    if(checkLenght){
      const base64Length = base64Data.length;
      const estimatedSizeInMB = (base64Length * 0.75) / (1024 * 1024);
      if (estimatedSizeInMB > 50) {
        throw new Error(
          `Base64 data too large: ${estimatedSizeInMB.toFixed(2)}MB (max 50MB)`
        );
      }
    }

    // Get authorization token from localStorage or other storage
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("token") ||
      "";

    if (!token) {
      throw new Error("No authorization token found");
    }

    // 1. Get presigned URL from backend
    const response = await fetch(
      `${BASE_URL}upload/get-upload-urls`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder: folder,
          files: [{ fileName, fileType }],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get upload URL: ${response.status}`);
    }

    const data = await response.json();
    const { uploadUrl, fileUrl } = data.files[0];

    // 2. Convert base64 to blob with error handling
    let blob;
    try {
      const base64Response = await fetch(base64Data);
      if (!base64Response.ok) {
        throw new Error(
          `Failed to convert base64 to blob: ${base64Response.status}`
        );
      }
      blob = await base64Response.blob();

      // Validate blob size
      if (blob.size === 0) {
        throw new Error("Generated blob is empty");
      }

      // 3. Compress the image if it's an image type
      // COMMENTED OUT: No need to compress images before uploading to S3
      // if (fileType.startsWith("image/")) {
      //   try {
      //     const options = {
      //       maxSizeMB: 1, // Maximum file size in MB
      //       maxWidthOrHeight: 1920, // Maximum width or height
      //       useWebWorker: true, // Use web worker for better performance
      //       fileType: fileType, // Maintain the original file type
      //     };

      //     // Convert blob to File object for compression
      //     const file = new File([blob], fileName, { type: fileType });
      //     const compressedBlob = await imageCompression(file, options);

      //     // Use compressed blob if it's smaller
      //     if (compressedBlob.size < blob.size) {
      //       blob = compressedBlob;
      //     }
      //   } catch (compressionError) {
      //     console.warn(
      //       "Image compression failed, using original:",
      //       compressionError
      //     );
      //     // Continue with original blob if compression fails
      //   }
      // }
    } catch (blobError) {
      throw new Error(`Base64 conversion failed: ${blobError.message}`);
    }

    // 4. Upload to S3 using presigned URL with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 second timeout (2 minutes)

    try {
      const s3Response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": fileType,
        },
        body: blob,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.status}`);
      }
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === "AbortError") {
        throw new Error("S3 upload timed out after 120 seconds");
      }
      throw fetchError;
    }

    return fileUrl; // Return the permanent S3 URL
  } catch (error) {
    throw error;
  }
};
